import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  Content,
  FlyoutSearchComponent,
  SEARCH_FEED
} from '@com-delta-idp/mnx-sitewide-header-ui';
import {
  ApprovedQuestion,
  AuxiliaryNavigationAemItem,
  CookieHelperService,
  QuestionBankService,
  SPECIAL_CHARACTERS_REGEX
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import {} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { PushPipe } from '@ngrx/component';
import { skip, take, takeUntil } from 'rxjs/operators';
import { FlyoutSearchSuperWrapperComponent } from './flyout-search-super-wrapper.component';

@Component({
  selector: 'idp-flyout-search-wrapper',
  standalone: true,
  imports: [CommonModule, FlyoutSearchComponent, PushPipe],
  templateUrl: './flyout-search-wrapper.component.html',
  styleUrls: ['./flyout-search-wrapper.component.scss']
})
export class FlyoutSearchWrapperComponent
  extends FlyoutSearchSuperWrapperComponent
  implements OnDestroy, OnInit
{
  @Input() displayFooterSearch = false;
  @Input() displayFlyout = false;
  @Output() closeFlyoutSearch = new EventEmitter();
  headerFooterSearch: AuxiliaryNavigationAemItem | undefined = {} as
    | AuxiliaryNavigationAemItem
    | undefined;

  private questionBankService = inject(QuestionBankService);
  private cookieHelper = inject(CookieHelperService);
  filteredSuggestions: string[] = [];

  ngOnInit(): void {
    this.loadHeaderContent();
    this.loadApprovedQuestions();
    this.subscribeToSearchResults();
  }

  closeFlyout(): void {
    this.closeFlyoutSearch.emit();
  }

  onSearchValueChange(term: string): void {
    this.terms$.next(term);
    if (term.trim().length >= 2) {
      this.questionBankService.search(term);
    } else {
      this.questionBankService.search('');
    }
  }

  submitSearch(value: string): void {
    const baseUrl = (this.siteSearchUrl || '/sitesearch?searchTerm=').replace(
      /^'+|'+$/g,
      ''
    );
    const url =
      baseUrl + encodeURIComponent(value.replace(SPECIAL_CHARACTERS_REGEX, ''));
    this.updateAnalytics(url);
    window.location.href = url;
  }

  updateFlyoutSearchContent(partialUpdate: Partial<Content>): void {
    this.flyoutContent$.next({
      ...this.flyoutContent$.getValue(),
      ...partialUpdate
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.isReady = false;
  }

  private loadHeaderContent(): void {
    this.headerFooter
      .getHeaderFooterMachCoreContent$()
      .pipe(take(1))
      .subscribe((response) => {
        if (response) {
          this.headerFooterSearch = response.header.auxiliaryNavigation.find(
            (item) => item.id === SEARCH_FEED
          );
          if (this.headerFooterSearch) {
            this.updateFlyoutSearchContent(
              this.createFlyoutSearchContent(this.headerFooterSearch)
            );
            this.siteSearchUrl = this.headerFooterSearch.aiSiteSearchUrl || '';
          }
          this.isReady = true;
        }
      });
  }

  private loadApprovedQuestions(): void {
    const prefUI = this.cookieHelper.getCookieValue('prefUI') || 'en-us';
    const languageCode = prefUI.split('-')[0] || 'en';
    this.questionBankService
      .getApprovedQuestions(languageCode)
      .pipe(take(1))
      .subscribe((questions: ApprovedQuestion[]) => {
        this.filteredSuggestions = questions
          .slice(0, 8)
          .map((q) => q.questionText);
      });
  }

  private subscribeToSearchResults(): void {
    this.questionBankService.filteredResults$
      .pipe(skip(1), takeUntil(this.unsubscribe$))
      .subscribe((results) => {
        this.filteredSuggestions = results;
      });
  }
}
